import { lazy, createElement } from "react";
import { RouteObject, Navigate, isRouteErrorResponse } from 'react-router-dom';
import MainLayout from '../layouts/MainLayout';
import CleanLayout from '../layouts/CleanLayout';
import Home from '../pages/Home';
import VideoDetails from '../pages/Video';
import Shorts from '../pages/Shorts';
import ErrorPage from '../pages/ErrorPage';
import { DirectionProvider } from '../contexts/DirectionContext';
import { ThemeProvider } from '../contexts/ThemeContext';
import { BrandingProvider } from '../contexts/BrandingContext';
import { SearchPage } from '../pages/SearchPage';
import Topics from '../pages/Topics';
import Magazine from '../pages/Magazine';
import AskRabbi from '../pages/AskRabbi';
import Donations from '../pages/Donations';
import Lives from '../pages/Lives';
import PrivacyPolicy from '../pages/PrivacyPolicy';
import TermsOfService from '../pages/TermsOfService';
import { ErrorCode } from '../types/errors';

// Define route paths as constants to avoid typos
export const ROUTES = {
  HOME: '/',
  VIDEO_DETAILS: '/video/:videoId', 
  SHORTS: '/shorts/:shortId',
  SEARCH: '/search',
  TOPICS: '/topics',
  MAGAZINE: '/magazine',
  ASK_RABBI: '/ask',
  DONATIONS: '/donate',
  LIVE: '/live',
  PRIVACY_POLICY: '/privacy-policy',
  TERMS_OF_SERVICE: '/terms-of-service',
} as const;

// Wrap component with providers
const withProviders = (Component: React.ComponentType | React.FC) => {
  const WrappedComponent = () => (
    <BrandingProvider>
      <ThemeProvider>
        <DirectionProvider>
          <Component />
        </DirectionProvider>
      </ThemeProvider>
    </BrandingProvider>
  );
  return <WrappedComponent />;
};

// Type-safe route configuration
export const routes: RouteObject[] = [
  // Main routes with header and footer
  {
    path: ROUTES.HOME,
    element: withProviders(MainLayout),
    errorElement: withProviders(ErrorPage),
    children: [
      {
        index: true,
        element: withProviders(Home),
      },
      {
        path: ROUTES.VIDEO_DETAILS,
        element: withProviders(VideoDetails),
      },
      {
        path: ROUTES.SEARCH,
        element: withProviders(SearchPage),
      },
      {
        path: ROUTES.TOPICS,
        element: withProviders(Topics),
      },
      {
        path: ROUTES.MAGAZINE,
        element: withProviders(Magazine),
      },
      {
        path: ROUTES.ASK_RABBI,
        element: withProviders(AskRabbi),
      },
      {
        path: ROUTES.DONATIONS,
        element: withProviders(Donations),
      },
      {
        path: ROUTES.LIVE,
        element: withProviders(Lives),
      },
      {
        path: ROUTES.PRIVACY_POLICY,
        element: withProviders(PrivacyPolicy),
      },
      {
        path: ROUTES.TERMS_OF_SERVICE,
        element: withProviders(TermsOfService),
      },
    ],
  },
  // Clean layout routes (no header/footer)
  {
    element: withProviders(CleanLayout),
    children: [
      {
        path: ROUTES.SHORTS,
        element: withProviders(Shorts),
      },
      {
        path: '*',
        element: withProviders(ErrorPage),
      }
    ]
  },
  // Catch-all route for 404s
  {
    path: '*',
    element: withProviders(ErrorPage),
  }
]; 